import React, { useCallback, useEffect, type FC } from "react";
import { Button } from "@wbly/ui";
import type { GlobalContent } from "@wbly/data-storefront";
import { useStore } from "@nanostores/react";
import { interpolateText } from "../../../util/interpolate-text";
import {
  $pagination,
  $resultsCount,
} from "../../../pages/[locale]/personalized-books/stores/filters.store";
import styles from "./ActionBar.module.css";

const { loadMore } = styles;

type LoadMoreProps = {
  buttonText?: string;
  content?: GlobalContent["productListing"];
  action?: () => void;
  isLastPage?: boolean;
};

export const LoadMore: FC<LoadMoreProps> = (props) => {
  const pagination = useStore($pagination);
  const { content, action } = props;
  const { loadMoreButton, loadMoreProgression } = content;
  const [progressionText, setProgressionText] = React.useState("");
  const totalResults = useStore($resultsCount);

  useEffect(() => {
    const currentShowing = pagination.current * pagination.totalVisibleProducts;
    const hasReachedEnd = currentShowing >= totalResults;

    const interpolatedText = interpolateText(loadMoreProgression, {
      current_count: hasReachedEnd ? totalResults : currentShowing,
      total: totalResults,
    });
    setProgressionText(interpolatedText);
  }, [pagination, loadMoreProgression, totalResults]);

  const handleClick = useCallback(() => {
    action();
  }, [action]);

  const isLastPage =
    pagination.current * pagination.totalVisibleProducts >= totalResults;

  return (
    // We need this html structure as this will be all wrapped in a button
    // by Algolia. This will keep the html semantic and enable us to reuse the
    // style of our button without recreating it.
    <span className={loadMore}>
      <em>{progressionText}</em>
      <Button
        as="em"
        outline
        data-testid="listing-load-more"
        onClick={handleClick}
        disabled={isLastPage}
        aria-disabled={isLastPage}
      >
        {loadMoreButton}
      </Button>
    </span>
  );
};
